import type { TFunction } from 'i18next';

import {
  CLASS_PACKAGE_TYPE_VALUES,
  PACKAGE_TYPE_LABEL_KEYS,
} from '@kit/bookings/constants';

/**
 * Shared thresholds for the "View details" overlay that opens the full
 * description + benefits list. Used by `/book`, `/book/packages`, and
 * `/packages` card surfaces so the same package shows the trigger
 * consistently across all three views.
 */
export const PACKAGE_FEATURES_PREVIEW_COUNT = 4;
export const PACKAGE_DESCRIPTION_OVERFLOW_CHARS = 120;

/**
 * Picks the right description/features pair for the active i18n language.
 *
 * Tenants author the primary copy in `description` / `features` (typically
 * English) and the optional secondary-language copy in `description_alt` /
 * `features_alt` (typically Arabic, but the schema is locale-agnostic).
 *
 * When the active language is the primary (`en`), we always render the
 * primary columns. For any other language we try `_alt` first and fall
 * back to the primary if the tenant hasn't authored the translation.
 */
export function selectPackageCopy(
  pkg: {
    description?: string | null;
    description_alt?: string | null;
    features?: string[] | null;
    features_alt?: string[] | null;
  },
  language: string,
  primaryLanguage: string = 'en',
): { description: string | null; features: string[] } {
  const isAlt = language !== primaryLanguage;
  const alt = (pkg.description_alt ?? '').trim();

  // Strip blank/whitespace-only entries from both lists. The admin drawer's
  // onBlur already cleans them, but legacy rows or service-layer writes can
  // bypass that, and rendering an empty <li> looks broken.
  const altFeatures = (pkg.features_alt ?? []).filter((f) => f.trim() !== '');
  const primaryFeatures = (pkg.features ?? []).filter((f) => f.trim() !== '');

  return {
    description: isAlt
      ? (alt.length > 0 ? alt : (pkg.description ?? null))
      : (pkg.description ?? null),
    features: isAlt && altFeatures.length > 0 ? altFeatures : primaryFeatures,
  };
}

export function formatPackageLabel(
  t: TFunction,
  packageType: string | null | undefined,
  bookingCount: number,
  isBundle = false,
): string {
  // Pre-bundle callers that haven't been threaded with isBundle yet still
  // detect parents via bookingCount === 0 (parents store 0 in the table).
  // Once the catalog rolls sibling credits up into the parent's count, the
  // explicit isBundle flag is what keeps the "Bundle" wording.
  const treatAsBundle = isBundle || bookingCount <= 0;
  const safeCount = bookingCount > 0 ? bookingCount : 0;

  const rawType = packageType ?? 'trainer';

  // Resolve the type label via the canonical value→key map (bookings namespace,
  // business-type aware). Unknown/legacy values fall back to title-casing.
  const labelKey = PACKAGE_TYPE_LABEL_KEYS[rawType];
  const typeLabel =
    (labelKey ? t(labelKey) : '') ||
    rawType
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const isClassesType = CLASS_PACKAGE_TYPE_VALUES.includes(rawType);

  const i18nKey = treatAsBundle
    ? isClassesType
      ? 'bundleClasses'
      : 'bundleBookings'
    : isClassesType
      ? 'packageClasses'
      : 'packageBookings';

  return t(i18nKey, { type: typeLabel, count: safeCount });
}
